<!--
================================================
Un composant qui affiche un graphique en barre indiquant la durée travaillé pour chaque unité d'une période, par exemple la durée de travail quotidienne pour la semaine actuelle.
(Ce composant est inclut dans TimerecordActivityComp).

Url d'accès : http://localhost:8080/fr/cronocloud-inc/timeclock

================================================
-->


<!------------------------------------------------
+++++++++++++++++++++ HTML +++++++++++++++++++++++
-------------------------------------------------->

<template>
    <div id="ChartTrActivityComp">

        <div v-show="!componentIsReady">
            <spinner-loader-comp></spinner-loader-comp>
        </div>

        <canvas v-bind:class="{loadingChart:!componentIsReady}" id="chart-tr-activity"></canvas>

    </div>
</template>

<!------------------------------------------------
+++++++++++++++++++ JAVASCRIPT++++++++++++++++++++
-------------------------------------------------->


<script>
    import Chart from 'chart.js';
    import {mapState} from 'vuex'
    import TimeMixin from "../mixins/TimeMixin.js";
    import SpinnerLoaderComp from "./SpinnerLoaderComp.vue";

    import _ from 'lodash'

    export default {

        name: 'ChartTrActivityComp', //utile dans vue dev tool

        mixins: [TimeMixin],
        components: {SpinnerLoaderComp},
        props: ['chartdata', 'options',],
        data: function () {
            return {

                currentChart : null, // l'objet chartJs du chart actuel
                periodUnit: [], // un array composé des unités de la periode,  ex : tous les jours de la semaine en cours pour thisWeek

                datasMerged: null, // égal à this.datas mais après passage dans mergePeriodUnitAndData, contient tous les jours de la période

                periods: { // les informations sur les périodes

                    lastWeek: {
                        name: 'lastWeek',
                        start: this.$moment().subtract(1, 'week').startOf('week'), // la date de début (objet moment)
                        end: this.$moment().subtract(1, 'week').endOf('week'),  // la date de fin  (objet moment)
                        unit: 'day', // les unités qui composent la période
                        parts: [], // un array composé des unités comprises entre start et end (ex: tous les jours de la semaine en cours)
                        duration : 7, // la durée en jour
                        barWidth: 35, // largeur des barres du chart
                        labelMaxNumber: 7, // le nombre de label maximum affichés sur l'axe X (ex : pour éviter que un chart thisMonth ne soit trop chargés),
                        showTooltip: true // indique si un tooltip doit être affiché

                    },
                    thisWeek: {
                        name:'thisWeek',
                        start: this.$moment().startOf('week'),
                        end: this.$moment().endOf('week'),
                        unit: 'day',
                        parts: [],
                        duration : 7,
                        barWidth: 35,
                        labelMaxNumber: 7,
                        showTooltip: true
                    },
                    thisMonth: {
                        name:'thisMonth',
                        start: this.$moment().startOf('month'),
                        end: this.$moment().endOf('month'),
                        unit: 'day',
                        parts: [],
                        duration : this.$moment().daysInMonth(),
                        barWidth: 10,
                        labelMaxNumber: 11,
                        showTooltip: true
                    },
                    lastMonth: {
                        name:'lastMonth',
                        start: this.$moment().subtract(1,'month').startOf('month'),
                        end: this.$moment().subtract(1,'month').endOf('month'),
                        unit: 'day',
                        parts: [],
                        duration : this.$moment().subtract(1,'month').daysInMonth(),
                        barWidth: 10,
                        labelMaxNumber: 11,
                        showTooltip: true
                    }


                },

                labels: null, // les labels du chart
                values: null, // les valeurs du chart
                componentIsReady : false, // indique quand le composant est prêt à être afficher
                locale: this.$route.params.locale, // la locale

            }
        },
        watch: {

            periodName: function(){
                // quand la période est modifiée (au clic sur .periodSelector dans le parent) on passe componentIsReady à false pour afficher un loader pendant le chargement du chart de la nouvelle période
                this.componentIsReady = false

            },
            datas: function () {

                // on crée un tableau contenant toutes les unités (ex jours) pour a période
                this.periodUnit = this.createPeriode(this.periodName);

                // on merge les datas aves les unités (ex :jours) de la période
                this.mergePeriodUnitAndData(this.datas[this.periodName], this.periodUnit)

                // on formate les data pour le chart
                this.formatData(this.datasMerged)

                // si un objet currentChart existe déjà on le supprime (évite bug multi charts)
                this.currentChart != null ? this.currentChart.destroy() : '';
                this.componentIsReady = true
                this.currentChart = this.createChart('chart-tr-activity')
            },

        },
        computed: {

            // computed props for store :
            ...mapState({

                // le nom de la période à afficher dans le chart
                periodName: state => state.TimerecordsActivityChartsComp.periodName,

                // les datas pour générer le graphiques (brut peut manquer des unités dans la période à ce stade car avant passage dans mergePeriodUnitAndData)
                datas: function(state){

                    return {
                        thisWeek: state.TimerecordsActivityChartsComp.durations.thisWeek,
                        thisMonth: state.TimerecordsActivityChartsComp.durations.thisMonth,
                        lastWeek: state.TimerecordsActivityChartsComp.durations.lastWeek,
                        lastMonth: state.TimerecordsActivityChartsComp.durations.lastMonth
                    }
                },

            }),

            //----------------------------------------------

            // les labels formatés pour afficher dans le chart
            formatedLabels: {
                    get () {return this.labels},
                    set: function (newValue) {this.labels = newValue}
            },
            // les valeurs formatées pour afficher dans le chart
            formatedValues: {
                    get () {return this.values},
                    set: function (newValue) {this.values = newValue}
            },

        },


        methods: {

            /**
             * Créé un tableau contenant tous les jours pour la période periodName
             *  exemple pour thisWeek -> [un tableau avec tous les jours de la semaine actuelle]
             * @param periodName string | Le nom de la période à créer
             * @return {Array}
             */
            createPeriode(periodName){

                        // on créer un tableau contenant tous les jours (unités) de la période en se basant sur start et end
                        let periodUnits = []

                        // on créé un jour de ref que l'on incrémentera
                        let currentDay = this.periods[periodName].start.clone()

                        for (let i = 0; i < this.periods[periodName].duration ; i++) {

                            if(i==0){ // au 1er tour on utilise this.periods.thisWeek.start
                                periodUnits.push (this.periods[periodName].start);

                            }else{
                                // au dela du 1er tour... on incrémente le jour
                                let plus1day = currentDay.add(1,'day')
                                periodUnits.push(plus1day.clone());
                            }
                        }
                        return periodUnits
            },

            /**
             * Cette méthode permet d'afficher les jours pour lesquels il n'y a pas de données dans le chart.
             * En effet l'api ne peut pas retourner de timerecords pour tous les jours de la période pour la simple raison que un utilisateur peut ne pas pointer chaque jour. En revanche dans le chart on souhaite pouvoir afficher tous le jours et matérialisé même ceux sans timerecords.
             *
             * Cette méthode permet donc de reformater datas afin que l'objet contienne tous le jours de la période
             *
             * @param periodUnit array | un array d'objet moment (Représente toutes les unités (ex : jours de la période)
             * @param datas object | un objet contenant les durées par unité pour la période au format :
             *  {2019-07-22T00:00:00+02:00: {duration: x, duration4Human}, ...}
             * */
            mergePeriodUnitAndData(datas,periodUnit){

                let datasMerged = {}; // création de l'objet final

                // on boucle sur tous les jours de la période pour faire un nouvel objet

                // rappel : periodUnit est au format suivant :{2019-07-22T00:00:00+02:00: {…}, 2019-07-23T00:00:00+02:00: {…}… }, les dates sont les clés des valeurs
                periodUnit.forEach(function (unit) {

                    // si datas est vide on retourne un tableau avec des durées de 0
                    if(_.isEmpty(datas)){
                        datasMerged[unit.format()] = {duration: 0, duration4Human: '0 h'}
                    }

                    else {

                        // on boucle sur tous jours dans datas (issus des tr), si une des clés correspond à la valeur de l'unité en cours (du jour en cours) cela veut dire que le jour est présent dans les datas
                        _.forEach(datas, function (data, key) {

                            // si le jour existe déjà -> on copie sa durée
                            if (key === unit.format()) {
                                datasMerged[unit.format()] = {
                                    duration: data.duration,
                                    duration4Human: data.duration4Human
                                }
                                return false; //  break le foreach

                            } else {// si le jour n'existe pas -> on ajoute une durée 0
                                datasMerged[unit.format()] = {duration: 0, duration4Human:  '0 h'}
                            }
                        })

                    }
                })

                this.datasMerged = datasMerged

            },

            /**
             * Formate datas pour afficher les labels et les valeur dans le chart
             * @param datas
             */
            formatData(datas){

                let labels = [];
                let values = [];
                let self = this
                _.forEach(datas, function(value, key) {

                    // On ajoute la date et la durée au tableau (sur 2 lignes)
                    labels.push([self.formatDateWithDayName(key,self.locale,false), value.duration4Human])
                    values.push(value.duration / 10000)  // division par dix mille pour rendre les valeurs plus lisible pour un humain
                });

                this.formatedLabels = labels
                this.formatedValues = values

            },
            /**
             * Créé le graphique chart js
             * Inspiré de https://alligator.io/vuejs/vue-chart-js/
             *
             * @param chartId {string} Identifiant du chart
             */
            createChart(chartId) {

                const ctx = document.getElementById(chartId);
                ctx.height = 250; // hauteur du chart

                Chart.defaults.global.defaultFontColor = '#81999f'; // changera la couleur de tout les textes 'mais je l'utilise pour changer la couleur des labels des datasets
                // (https://www.chartjs.org/docs/latest/general/fonts.html)

                const myChart = new Chart(ctx, {
                    type: 'bar',
                    data: {

                        // pour écrire sur 2 ligne il faut faire des array imbriqués :
                        // labels: [['lun. 24 juil.', '8h']],
                        labels: this.formatedLabels,
                        datasets: [
                            {
                            // label: 'My First dataset',
                            backgroundColor: '#e2e6e7',
                            hoverBackgroundColor: '#e2e6e7',

                            data: this.formatedValues,
                                borderWidth: 1,
                                borderColor:'#bec8ca',
                            },
                        ]},
                    options: {
                        legend:
                            //https://www.chartjs.org/docs/latest/configuration/legend.html
                            {
                                display: false, //masque légende
                                // position: 'bottom'
                            },
                        tooltips: {
                            enabled:this.periods[this.periodName].showTooltip,
                            callbacks: {  // pour customiser la tooltip vu sur https://stackoverflow.com/questions/43604597/how-to-customize-the-tooltip-of-a-chart-js-2-0-doughnut-chart
                                title: function(tooltipItem, data) {
                                    return data['labels'][tooltipItem[0]['index']];
                                },
                                label: function(){return null},
                            },
                        },
                        layout:{
                            padding: {top: 30, right: 30 , bottom : 10, left :30} //marge intérieure https://www.chartjs.org/docs/latest/configuration/layout.html#padding
                        },

                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            xAxes: [{
                                // barPercentage: 0.5,
                                barThickness: this.periods[this.periodName].barWidth,
                                // maxBarThickness: 30,
                                // minBarLength: 30,
                                gridLines: {
                                    // drawBorder: false,
                                    // offsetGridLines: true,
                                    // color: 'red',
                                    drawBorder: false,
                                    display: false,

                                    zeroLineColor: 'transparent', // couleur de la ligne du bas

                                },
                                ticks: {
                                    autoSkip: true,
                                    maxTicksLimit: this.periods[this.periodName].labelMaxNumber // on limite le nombre de labels affiché sur l'axe x
                                }
                            }],
                            yAxes: [{
                                ticks: {
                                    display: false, // affichage de l'échelle
                                    // autoSkip: true,
                                    // maxTicksLimit: 6// on limite le nombre de labels affiché sur l'axe y
                                },
                                // scaleLabel: {
                                //     display: true,
                                //     labelString: 'foobar...' // pour écrire sur l'axe y
                                // },
                                gridLines: {
                                    display: false,
                                    // color: 'green', // pur débug car pas affiché
                                    drawBorder: false, // masque les bordure y
                                    offsetGridLines: true,
                                    // color: 'white',
                                },

                            }]
                        }
                    },
                });

                return myChart
            },

        },

        mounted () {

        }
    }
</script>

<!------------------------------------------------
++++++++++++++++++++++ CSS +++++++++++++++++++++++
-------------------------------------------------->

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
    @import "../assets/css/globalVar.scss";
    /*@import "../assets/css/bulmaResponsiveDep.scss";*/

    #ChartTrActivityComp {
        position: relative; // pour positionner .spinner

        .loadingChart{
            opacity: 0.5;
        }
        .spinnerLoaderComp {
            // le loader est positionné en relatif devant le chart
            width: 20%;
            /*border: dashed 1px #cccccc;*/
            position: absolute;
            z-index: 4; // pour faire passer le loader devant le chart
            left: 40%;
            top: 70px;

        }

        #chart-tr-activity {

            height: 250px; // définit la hauteur du graphique (requiert "maintainAspectRatio" à false)
            /*padding: 1rem;*/
        }
    }
</style>
